import { useEffect, useMemo, useState } from "react";
import { BRANCH } from "../constants/api";
import useApiMethods from "../lib/useApiMethods";
import { BranchCell, TempTransferBadge } from "../components/common/BranchColumn";
import { isOrgAdminUser, shouldShowOrgAdminBranchUI } from "../utils/branchSession";
import { SESSION_STORAGE_HYDRATED_EVENT } from "./use-navigation";

/**
 * Org-admin branch column + dropdown filter for list tables.
 * Returns helpers to inject a Branch column after a named accessor.
 */
export function useOrgAdminBranchColumn(afterAccessor = "employee_name") {
    const { fetch } = useApiMethods();
    const [branches, setBranches] = useState([]);

    const isOrgAdmin = isOrgAdminUser();
    const hideBranchFilter = shouldShowOrgAdminBranchUI();
    const showBranchColumn = isOrgAdmin && branches.length > 0;

    const branchFilterOptions = useMemo(
        () => branches.map((branch) => ({
            value: String(branch.id),
            label: branch.is_main_branch ? `${branch.name} (Main)` : branch.name,
        })),
        [branches]
    );

    const branchColumnDef = useMemo(() => {
        if (!showBranchColumn) return null;
        return {
            accessor: "branch",
            Header: "Branch",
            sortable: true,
            // Header branch switcher already scopes the list — a second
            // Branch dropdown in the filter sidebar fights that selection.
            filterable: !hideBranchFilter,
            filterType: "select",
            filterOptions: branchFilterOptions,
            width: "120px",
            maxWidth: "160px",
            Cell: ({ row }) => <BranchCell record={row.original || row} />,
        };
    }, [showBranchColumn, hideBranchFilter, branchFilterOptions]);

    useEffect(() => {
        const loadBranches = () => {
            if (!isOrgAdminUser()) {
                setBranches([]);
                return;
            }
            fetch(BRANCH, { drop_down: "True" })
                .then((response) => {
                    if (response?.status !== "success") {
                        setBranches([]);
                        return;
                    }
                    const raw = response?.data;
                    const list = Array.isArray(raw)
                        ? raw
                        : (raw?.result || raw?.data || []);
                    setBranches(
                        Array.isArray(list)
                            ? list.filter((b) => b.is_active !== false)
                            : []
                    );
                })
                .catch(() => setBranches([]));
        };

        loadBranches();
        window.addEventListener(SESSION_STORAGE_HYDRATED_EVENT, loadBranches);
        return () => window.removeEventListener(SESSION_STORAGE_HYDRATED_EVENT, loadBranches);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const insertBranchColumn = (columns, accessor = afterAccessor) => {
        if (!branchColumnDef) return columns;
        const fallbacks = [accessor, "employee_name", "employee", "employee_details"];
        let idx = -1;
        for (const key of fallbacks) {
            idx = columns.findIndex((c) => c.accessor === key);
            if (idx >= 0) break;
        }
        if (idx < 0) return [branchColumnDef, ...columns];
        return [
            ...columns.slice(0, idx + 1),
            branchColumnDef,
            ...columns.slice(idx + 1),
        ];
    };

    return {
        showBranchColumn,
        branchFilterOptions,
        branchColumnDef,
        insertBranchColumn,
        TempTransferBadge,
        BranchCell,
    };
}
